import React, { useCallback } from 'react';
import {
  Button,
  ButtonText,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { BulletItem } from '@/common/components/BulletList';
import { useDialog } from '@/common/context/DialogContext';
import { useGuardStudentAction } from '@/common/studentPreview/StudentPreviewSource';
import { DRY_RUN_UNAVAILABLE_MESSAGE } from '@/common/hooks/oralExam/useDryRunExaminerGate';
import { useEnterOralExamPracticeLobby } from '@/common/hooks/oralExam/useEnterOralExamPracticeLobby';
import log from '@/common/utils/logger';

type OralExamPracticeEntryProps = {
  /** Session-scoped: dry-run availability and ensure are for this oral-exam task. */
  taskId?: string;
};

/**
 * Starts the dry run for this oral-exam task. Availability and the examiner
 * handshake are checked only after the student clicks, never by hiding the entry.
 */
function useStartDryRun(taskId: string | undefined) {
  const { showToastMessage } = useDialog();
  const guardStudentAction = useGuardStudentAction();
  const {
    enterPracticeLobby,
    isEnteringPractice,
    enterPracticeError,
    unavailableAfterLastAttempt,
  } = useEnterOralExamPracticeLobby(taskId);

  const handleStart = useCallback(async () => {
    if (!taskId || isEnteringPractice) return;
    guardStudentAction(async () => {
      try {
        await enterPracticeLobby();
      } catch (error) {
        log.error(error);
      }
    });
  }, [enterPracticeLobby, guardStudentAction, isEnteringPractice, taskId]);

  React.useEffect(() => {
    if (enterPracticeError && !unavailableAfterLastAttempt) {
      showToastMessage('error', enterPracticeError);
    }
  }, [enterPracticeError, showToastMessage, unavailableAfterLastAttempt]);

  const start = () => {
    handleStart().catch((error) => {
      log.error(error);
    });
  };

  return {
    start,
    starting: isEnteringPractice,
    enterPracticeError,
    unavailableAfterLastAttempt,
  };
}

function DryRunStartFeedback({
  enterPracticeError,
  unavailableAfterLastAttempt,
}: {
  enterPracticeError?: string | null;
  unavailableAfterLastAttempt: boolean;
}) {
  const { t } = useTranslation();
  if (unavailableAfterLastAttempt) {
    return (
      <Text size="xs" color="$textSubtle" testID="oral-exam-practice-unavailable">
        {t('oralExam.practice.unavailable', { defaultValue: DRY_RUN_UNAVAILABLE_MESSAGE })}
      </Text>
    );
  }
  if (enterPracticeError) {
    return (
      <Text size="xs" color="$statusError" testID="oral-exam-practice-start-error">
        {enterPracticeError}
      </Text>
    );
  }
  return null;
}

/**
 * "Try a dry run" section with an outline Start button. Used where the dry run
 * is the task itself; inside a graded exam it is a line in "Before your exam"
 * (`OralExamDryRunBullet`).
 */
export function OralExamPracticeEntry({ taskId }: OralExamPracticeEntryProps) {
  const { t } = useTranslation();
  const {
    start, starting, enterPracticeError, unavailableAfterLastAttempt,
  } = useStartDryRun(taskId);

  if (!taskId) {
    return null;
  }

  return (
    <VStack space="sm" testID="oral-exam-practice-task-panel">
      <Heading size="sm" testID="oral-exam-practice-aside-heading">
        {t('oralExam.practice.asideTitle', { defaultValue: 'Try a dry run' })}
      </Heading>
      <Text size="sm" color="$textBody">
        {t('oralExam.practice.asideBody', {
          defaultValue:
            'Check your camera, microphone and ID photos, then answer two questions from this exam on your own. Spoken slots are placeholders like [some case], not the fillers you will get in the live exam. Nothing is graded, no record is kept.',
        })}
      </Text>
      <Button
        variant="outline"
        size="sm"
        action="secondary"
        alignSelf="flex-start"
        borderColor="$borderLight300"
        onPress={start}
        isDisabled={starting}
        testID="oral-exam-practice-start"
      >
        {starting ? (
          <HStack space="xs" alignItems="center">
            <Spinner size="small" />
            <ButtonText color="$textBody">
              {t('oralExam.practice.starting', { defaultValue: 'Starting…' })}
            </ButtonText>
          </HStack>
        ) : (
          <ButtonText color="$textBody">
            {t('oralExam.practice.startCta', { defaultValue: 'Start dry run' })}
          </ButtonText>
        )}
      </Button>
      <DryRunStartFeedback
        enterPracticeError={enterPracticeError}
        unavailableAfterLastAttempt={unavailableAfterLastAttempt}
      />
    </VStack>
  );
}

/**
 * The dry run as one optional line in "Before your exam": an offer for a first
 * oral exam, with the start as an inline link.
 */
export function OralExamDryRunBullet({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const {
    start, starting, enterPracticeError, unavailableAfterLastAttempt,
  } = useStartDryRun(taskId);

  return (
    <VStack space="xs" testID="oral-exam-practice-task-panel">
      <BulletItem size="sm">
        <Text flex={1} size="sm">
          {t('oralExam.beforeExam.dryRunBefore')}
          <Text
            size="sm"
            color="$textBrand"
            textDecorationLine="underline"
            accessibilityRole="link"
            accessibilityState={{ disabled: starting }}
            onPress={starting ? undefined : start}
            testID="oral-exam-practice-start"
          >
            {starting ? t('oralExam.practice.starting') : t('oralExam.beforeExam.dryRunLink')}
          </Text>
          {t('oralExam.beforeExam.dryRunAfter')}
        </Text>
      </BulletItem>
      <VStack pl="$3">
        <DryRunStartFeedback
          enterPracticeError={enterPracticeError}
          unavailableAfterLastAttempt={unavailableAfterLastAttempt}
        />
      </VStack>
    </VStack>
  );
}
